import { useEffect, useRef, useState } from 'react';
import { tokens } from '@/design';
import type { LayoutGraphicGeometry, LayoutGraphicPoint, LayoutGraphicType, PageRect } from './layoutTypes';
import { layoutGraphicTool } from './layoutGraphicTools';
import { sampleQuadraticCurve } from './layoutGraphicGeometry';

export interface DrawnLayoutGraphic {
  readonly type: LayoutGraphicType;
  readonly rect: PageRect;
  readonly geometry: LayoutGraphicGeometry;
}

interface Props {
  readonly tool: LayoutGraphicType;
  readonly pageWidthMm: number;
  readonly pageHeightMm: number;
  readonly onComplete: (graphic: DrawnLayoutGraphic) => void;
  readonly onCancel: () => void;
}

const MIN_MM = 1;

function frame(points: readonly LayoutGraphicPoint[]): PageRect {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x, y,
    width: Math.max(MIN_MM, Math.max(...xs) - x),
    height: Math.max(MIN_MM, Math.max(...ys) - y),
  };
}

function pathGraphic(type: LayoutGraphicType, source: readonly LayoutGraphicPoint[], closed: boolean): DrawnLayoutGraphic {
  const sampled = type === 'arc' && source.length === 3
    ? sampleQuadraticCurve(source[0], source[1], source[2])
    : source;
  const rect = frame(sampled);
  return {
    type,
    rect,
    geometry: {
      type: 'path',
      paths: [{
        closed,
        points: sampled.map((point) => ({
          x: (point.x - rect.x) / rect.width,
          y: (point.y - rect.y) / rect.height,
        })),
      }],
    },
  };
}

export function LayoutGraphicDrawingOverlay({
  tool, pageWidthMm, pageHeightMm, onComplete, onCancel,
}: Props) {
  const [points, setPoints] = useState<LayoutGraphicPoint[]>([]);
  const [hover, setHover] = useState<LayoutGraphicPoint | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const drawingRef = useRef(false);
  const meta = layoutGraphicTool(tool);

  useEffect(() => {
    setPoints([]);
    setHover(null);
    drawingRef.current = false;
    overlayRef.current?.focus();
  }, [tool]);

  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const pane = overlayRef.current?.closest('[data-testid="layout-workspace-pane"]');
      if (pane && !pane.contains(globalThis.document.activeElement)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      } else if (event.key === 'Enter' && meta.gesture === 'vertices' && points.length >= 2) {
        event.preventDefault();
        onComplete(pathGraphic(tool, points, tool === 'polygon' || tool === 'polygonText'));
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [meta.gesture, onCancel, onComplete, points, tool]);

  const pointFor = (event: {
    readonly currentTarget: HTMLDivElement;
    readonly clientX: number;
    readonly clientY: number;
  }): LayoutGraphicPoint => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(pageWidthMm, ((event.clientX - bounds.left) / bounds.width) * pageWidthMm)),
      y: Math.max(0, Math.min(pageHeightMm, ((event.clientY - bounds.top) / bounds.height) * pageHeightMm)),
    };
  };
  const completeBox = (start: LayoutGraphicPoint, end: LayoutGraphicPoint) => {
    const rect = frame([start, end]);
    onComplete({ type: tool, rect, geometry: { type: 'rect', rect: { x: 0, y: 0, width: 1, height: 1 } } });
  };
  const fixedCount = meta.gesture === 'threePoint' ? 3 : meta.gesture === 'fourPoint' ? 4 : 0;
  const preview = hover ? [...points, hover] : points;

  return (
    <div
      ref={overlayRef}
      data-testid="layout-graphic-drawing-overlay"
      role="region"
      aria-label={`Drawing ${meta.label}. Escape cancels${meta.gesture === 'vertices' ? '; double-click or Enter finishes' : ''}.`}
      tabIndex={0}
      onPointerDown={(event) => {
        event.stopPropagation();
        const point = pointFor(event);
        if (meta.gesture === 'point') {
          onComplete({ type: tool, rect: { x: point.x - 2, y: point.y - 2, width: 4, height: 4 }, geometry: { type: 'point', point: { x: 0.5, y: 0.5 } } });
        } else if (meta.gesture === 'box' || meta.gesture === 'freehand') {
          drawingRef.current = true;
          setPoints([point]);
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }
      }}
      onClick={(event) => {
        if (meta.gesture !== 'vertices' && !fixedCount) return;
        const point = pointFor(event);
        const next = [...points, point];
        setPoints(next);
        if (fixedCount && next.length === fixedCount) onComplete(pathGraphic(tool, next, false));
      }}
      onDoubleClick={(event) => {
        if (meta.gesture !== 'vertices') return;
        event.preventDefault();
        const next = points.length >= 2 ? points : [...points, pointFor(event)];
        if (next.length >= 2) onComplete(pathGraphic(tool, next, tool === 'polygon' || tool === 'polygonText'));
      }}
      onPointerMove={(event) => {
        const point = pointFor(event);
        setHover(point);
        if (drawingRef.current && meta.gesture === 'freehand') setPoints((current) => [...current, point]);
      }}
      onPointerUp={(event) => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        const end = pointFor(event);
        if (meta.gesture === 'box') completeBox(points[0] ?? end, end);
        else if (meta.gesture === 'freehand') {
          const next = [...points, end];
          if (next.length >= 2) onComplete(pathGraphic(tool, next, tool === 'lasso'));
        }
      }}
      style={{ position: 'absolute', inset: 0, zIndex: 100, cursor: 'crosshair', touchAction: 'none' }}
    >
      <svg aria-hidden viewBox={`0 0 ${pageWidthMm} ${pageHeightMm}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
        {preview.length ? (
          <polyline
            data-testid="layout-graphic-live-preview"
            points={preview.map((point) => `${point.x},${point.y}`).join(' ')}
            fill={tool === 'polygon' || tool === 'polygonText' || tool === 'lasso' ? tokens.color.accent.subtle : 'none'}
            stroke={tokens.color.accent.primary}
            strokeWidth=".7"
            strokeDasharray="2 1"
          />
        ) : null}
      </svg>
    </div>
  );
}
